import { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useFinance } from 'contexts/FinanceContext';
import { Transaction, TransactionType } from 'types/finance';

interface TransactionFormProps {
  isOpen: boolean;
  onClose: () => void;
  transaction?: Transaction | null;
}

const emptyForm = {
  type: 'expense' as TransactionType,
  amount: '',
  categoryId: '',
  description: '',
  date: new Date().toISOString().slice(0, 10),
};

export default function TransactionForm({
  isOpen,
  onClose,
  transaction,
}: TransactionFormProps) {
  const { addTransaction, updateTransaction, getCategoriesByType } =
    useFinance();
  const toast = useToast();
  const [form, setForm] = useState(emptyForm);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'navy.900');

  useEffect(() => {
    if (transaction) {
      setForm({
        type: transaction.type,
        amount: transaction.amount.toString(),
        categoryId: transaction.categoryId,
        description: transaction.description,
        date: transaction.date,
      });
    } else {
      setForm(emptyForm);
    }
  }, [transaction, isOpen]);

  const categories = getCategoriesByType(form.type);

  useEffect(() => {
    if (
      categories.length > 0 &&
      !categories.find((c) => c.id === form.categoryId)
    ) {
      setForm((prev) => ({ ...prev, categoryId: categories[0].id }));
    }
  }, [form.type, categories, form.categoryId]);

  const handleSubmit = () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      toast({ title: 'Enter a valid amount', status: 'error', duration: 3000 });
      return;
    }
    if (!form.categoryId) {
      toast({ title: 'Select a category', status: 'error', duration: 3000 });
      return;
    }

    const data = {
      type: form.type,
      amount,
      categoryId: form.categoryId,
      description: form.description.trim(),
      date: form.date,
      marked: transaction?.marked ?? false,
    };

    if (transaction) {
      updateTransaction(transaction.id, data);
      toast({ title: 'Transaction updated', status: 'success', duration: 3000 });
    } else {
      addTransaction(data);
      toast({ title: 'Transaction added', status: 'success', duration: 3000 });
    }
    onClose();
  };

  const fieldProps = {
    fontSize: 'sm' as const,
    fontWeight: '500' as const,
    bg: boxBg,
    border: '0px solid',
    borderRadius: '16px',
    h: '44px',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="20px" mx="16px">
        <ModalHeader color={textColor} fontSize="xl" fontWeight="700">
          {transaction ? 'Edit Transaction' : 'Add Transaction'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color={textColorSecondary}
              >
                Type
              </FormLabel>
              <Select
                {...fieldProps}
                value={form.type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    type: e.target.value as TransactionType,
                    categoryId: '',
                  })
                }
              >
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color={textColorSecondary}
              >
                Amount
              </FormLabel>
              <Input
                {...fieldProps}
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color={textColorSecondary}
              >
                Category
              </FormLabel>
              <Select
                {...fieldProps}
                value={form.categoryId}
                onChange={(e) =>
                  setForm({ ...form, categoryId: e.target.value })
                }
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color={textColorSecondary}
              >
                Date
              </FormLabel>
              <Input
                {...fieldProps}
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </FormControl>
            <FormControl gridColumn={{ md: 'span 2' }}>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color={textColorSecondary}
              >
                Description
              </FormLabel>
              <Textarea
                fontSize="sm"
                fontWeight="500"
                bg={boxBg}
                border="0px solid"
                borderRadius="16px"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Optional note..."
                rows={3}
              />
            </FormControl>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button
            variant="light"
            borderRadius="16px"
            fontSize="sm"
            fontWeight="500"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            borderRadius="16px"
            fontSize="sm"
            fontWeight="500"
            onClick={handleSubmit}
          >
            {transaction ? 'Update' : 'Add'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
